import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, BarChart3, ShoppingBag, Calendar, TrendingUp, CheckCircle, Quote } from 'lucide-react';

const caseStudies = [
  {
    id: 1,
    title: "Morrison Consulting",
    industry: "Professional Services",
    location: "Manchester, UK",
    challenge: "Outdated website with no online presence. Losing leads to competitors with modern sites. Phone-only enquiries meant missing opportunities outside business hours.",
    solution: "Built a professional website with SEO optimization, an AI chatbot for lead qualification, and an online booking system. Integrated automated follow-up sequences.",
    implementation: [
      "Modern responsive website design",
      "AI chatbot for 24/7 lead qualification", 
      "Online booking system integration",
      "SEO optimization for local searches",
      "Automated email follow-up sequences"
    ],
    results: [
      { label: "Inquiries Increase", value: "3x", description: "Monthly enquiries tripled within 3 months" },
      { label: "Google Ranking", value: "Page 1", description: "Now ranking for 15+ key search terms" },
      { label: "Time Saved Weekly", value: "12 hrs", description: "Automated responses handle routine queries" },
      { label: "Conversion Rate", value: "+45%", description: "More enquiries converting to consultations" }
    ],
    quote: "NEURA AI SOFTWARES built us a beautiful website, set up our online booking system, and even added an AI chatbot. Our inquiries have tripled and we're now visible on Google. It's transformed our business.",
    author: "James Morrison",
    role: "Owner",
    icon: BarChart3,
    gradient: "from-cyan-500 to-blue-600",
    timeline: "6 weeks",
    investment: "AI Revenue System"
  },
  {
    id: 2,
    title: "Chen's Home Decor",
    industry: "E-commerce & Retail",
    location: "Birmingham, UK",
    challenge: "Selling locally only with no online store. Missing out on nationwide customers. Manual order processing was time-consuming and error-prone.",
    solution: "Full e-commerce store with Stripe payments, inventory management, SEO, and automated email marketing. AI chatbot handles product enquiries.",
    implementation: [
      "Custom e-commerce platform build",
      "Stripe payment integration",
      "Automated inventory management",
      "Product recommendation AI",
      "Email marketing automation",
      "SEO for product pages"
    ],
    results: [
      { label: "Monthly Revenue", value: "+180%", description: "Revenue nearly tripled in 6 months" },
      { label: "Online Orders", value: "500+/mo", description: "From zero to 500+ monthly orders" },
      { label: "Customer Reach", value: "Nationwide", description: "Now shipping across the UK" },
      { label: "Return Customers", value: "35%", description: "Strong repeat purchase rate" }
    ],
    quote: "The e-commerce store they built is fantastic. Easy to manage, looks professional, and the SEO work means we're now on page 1 of Google. Our revenue has nearly tripled.",
    author: "David Chen",
    role: "Founder",
    icon: ShoppingBag,
    gradient: "from-purple-500 to-indigo-600",
    timeline: "8 weeks",
    investment: "Custom Software + AI Revenue System"
  },
  {
    id: 3,
    title: "Mitchell Physio Clinic",
    industry: "Healthcare & Wellness",
    location: "Belfast, UK",
    challenge: "Phone-only bookings causing missed appointments and admin overload. No online visibility. Staff spending hours on appointment reminders.",
    solution: "GDPR-compliant website with online booking, automated reminders, patient portal, and local SEO. AI handles appointment scheduling 24/7.",
    implementation: [
      "GDPR-compliant website design",
      "Online booking system",
      "Automated appointment reminders",
      "Patient portal for records",
      "Local SEO optimization",
      "AI scheduling assistant"
    ],
    results: [
      { label: "Bookings Increase", value: "3x", description: "Online bookings now exceed phone" },
      { label: "No-Show Reduction", value: "50%", description: "Automated reminders cut no-shows" },
      { label: "Admin Time Saved", value: "15 hrs/wk", description: "Staff freed for patient care" },
      { label: "New Patients", value: "+120%", description: "Google visibility driving growth" }
    ],
    quote: "Our appointment requests have tripled and we spend zero time on website maintenance. The automated reminders have cut no-shows in half. Best decision we made this year.",
    author: "Sarah Mitchell",
    role: "Owner",
    icon: Calendar,
    gradient: "from-emerald-500 to-teal-600",
    timeline: "5 weeks",
    investment: "AI Revenue System"
  },
  {
    id: 4,
    title: "Roberts Legal Services",
    industry: "Legal & Professional Services",
    location: "London, UK",
    challenge: "Competitors dominating Google searches. No way to capture leads outside business hours. High cost per lead from traditional marketing.",
    solution: "SEO-optimized website with an AI chatbot that qualifies leads 24/7 and books consultations automatically. Integrated with CRM for seamless follow-up.",
    implementation: [
      "SEO-optimized website redesign",
      "AI chatbot for lead qualification",
      "Automated consultation booking",
      "CRM integration",
      "Lead scoring system",
      "Automated nurture sequences"
    ],
    results: [
      { label: "Lead Capture", value: "24/7", description: "Never miss an enquiry again" },
      { label: "Qualified Leads", value: "+200%", description: "AI pre-qualifies before human contact" },
      { label: "Cost Per Lead", value: "-60%", description: "Dramatic reduction in acquisition cost" },
      { label: "Consultation Bookings", value: "+85%", description: "More leads converting to meetings" }
    ],
    quote: "The AI chatbot handles enquiries 24/7, qualifies leads, and books consultations. It's like having an extra staff member who never sleeps. Our cost per lead has dropped 60%.",
    author: "Emma Roberts",
    role: "Director",
    icon: TrendingUp,
    gradient: "from-rose-500 to-pink-600",
    timeline: "7 weeks",
    investment: "AI Revenue System + Custom Integration"
  }
];

const CaseStudiesPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-slate-900 to-slate-800 text-white py-6">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Link to="/" className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Client <span className="text-cyan-400">Success Stories</span>
          </h1>
          <p className="text-slate-300 text-lg max-w-2xl">
            Real results from UK businesses using our income growth software systems.
          </p>
        </div>
      </header>

      {/* Stats Banner */}
      <div className="bg-gradient-to-r from-cyan-600 to-blue-600 py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center text-white">
            <div>
              <div className="text-3xl md:text-4xl font-bold">100+</div>
              <div className="text-cyan-100 text-sm">UK Businesses Served</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold">3x</div>
              <div className="text-cyan-100 text-sm">Average Lead Increase</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold">24/7</div>
              <div className="text-cyan-100 text-sm">AI Response Time</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold">60%</div>
              <div className="text-cyan-100 text-sm">Cost Reduction</div>
            </div>
          </div>
        </div>
      </div>

      {/* Case Studies */}
      <main className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="space-y-16">
            {caseStudies.map((study, index) => {
              const IconComponent = study.icon;
              const isEven = index % 2 === 0;

              return (
                <article
                  key={study.id}
                  className="bg-white rounded-3xl border border-gray-200 overflow-hidden shadow-lg hover:shadow-xl transition-shadow"
                  data-testid={`case-study-detail-${study.id}`}
                >
                  {/* Header Bar */}
                  <div className={`h-2 bg-gradient-to-r ${study.gradient}`} />
                  
                  <div className="p-8 lg:p-12">
                    {/* Company Info */}
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 mb-8">
                      <div className="flex items-start gap-4">
                        <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${study.gradient} flex items-center justify-center shadow-lg flex-shrink-0`}>
                          <IconComponent className="w-8 h-8 text-white" />
                        </div>
                        <div>
                          <h2 className="text-2xl lg:text-3xl font-bold text-gray-900">{study.title}</h2>
                          <p className="text-gray-500">{study.industry} • {study.location}</p>
                          <div className="flex items-center gap-4 mt-2 text-sm">
                            <span className="px-3 py-1 bg-cyan-100 text-cyan-700 rounded-full font-medium">{study.investment}</span>
                            <span className="text-gray-400">Timeline: {study.timeline}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Challenge & Solution Grid */}
                    <div className="grid lg:grid-cols-2 gap-8 mb-10">
                      <div className="bg-red-50 rounded-2xl p-6 border border-red-100">
                        <h3 className="text-red-600 font-bold uppercase tracking-wider text-sm mb-3">The Challenge</h3>
                        <p className="text-gray-700 leading-relaxed">{study.challenge}</p>
                      </div>
                      <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-100">
                        <h3 className="text-emerald-600 font-bold uppercase tracking-wider text-sm mb-3">Our Solution</h3>
                        <p className="text-gray-700 leading-relaxed">{study.solution}</p>
                      </div>
                    </div>

                    {/* Implementation */}
                    <div className="mb-10">
                      <h3 className="text-gray-900 font-bold text-lg mb-4">What We Implemented</h3>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {study.implementation.map((item, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-gray-600">
                            <CheckCircle className="w-5 h-5 text-cyan-500 flex-shrink-0" />
                            <span className="text-sm">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Results Grid */}
                    <div className="mb-10">
                      <h3 className="text-gray-900 font-bold text-lg mb-4">The Results</h3>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {study.results.map((result, idx) => (
                          <div key={idx} className="bg-gray-50 rounded-xl p-5 border border-gray-100 text-center">
                            <div className={`text-3xl font-bold bg-gradient-to-r ${study.gradient} bg-clip-text text-transparent`}>
                              {result.value}
                            </div>
                            <div className="text-gray-900 font-medium text-sm mt-1">{result.label}</div>
                            <div className="text-gray-500 text-xs mt-2">{result.description}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Quote */}
                    <div className={`bg-gradient-to-br ${study.gradient} rounded-2xl p-8 text-white relative overflow-hidden`}>
                      <Quote className="absolute top-4 left-4 w-12 h-12 text-white/20" />
                      <blockquote className="relative z-10">
                        <p className="text-lg lg:text-xl italic mb-6 leading-relaxed">"{study.quote}"</p>
                        <footer className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                            <span className="font-bold text-lg">
                              {study.author.split(' ').map(n => n[0]).join('')}
                            </span>
                          </div>
                          <div>
                            <div className="font-semibold">{study.author}</div>
                            <div className="text-white/80 text-sm">{study.role}, {study.title}</div>
                          </div>
                        </footer>
                      </blockquote>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </main>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-slate-900 to-slate-800 py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Be Our Next Success Story?
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            Get your free AI revenue audit and discover how we can help your business grow.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold px-8 py-4 rounded-full hover:from-cyan-600 hover:to-blue-700 transition-all shadow-lg"
            >
              Get Your Free AI Audit
              <ArrowUpRight className="w-5 h-5" />
            </Link>
            <a
              href="https://wa.me/447471343190"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-500 text-white font-semibold px-8 py-4 rounded-full transition-all"
            >
              Chat on WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <p>© {new Date().getFullYear()} NEURA AI SOFTWARES. All rights reserved.</p>
          <div className="flex justify-center gap-6 mt-4 text-sm">
            <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-white transition-colors">Terms of Service</Link>
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CaseStudiesPage;
